import { Icon, IconButton, MenuItem, Select, Typography } from '../../core'
import { TablePaginationProps } from '../../core/TablePagination'
import { styled } from '../../styles'
import { BaseThemeType, useMediaQuery } from '../../theme'

type ButtonProps = {
  onClick: () => void
  disabled?: boolean
}

export const NextButton = (props: ButtonProps) => {
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down(393))
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.between(393, 'lg'))

  function getIconSize() {
    if (isMobile) return 'xXSmall'
    if (isTablet) return 'small'
    return 'medium'
  }

  return (
    <IconButton disabled={props.disabled} onClick={props.onClick} variant="outlined" color="info" size={getIconSize()}>
      <Icon icon={'arrow_right_alt'}></Icon>
    </IconButton>
  )
}

export const PreviousButton = (props: ButtonProps) => {
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down(393))
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.between(393, 'lg'))

  function getIconSize() {
    if (isMobile) return 'xXSmall'
    if (isTablet) return 'small'
    return 'medium'
  }

  return (
    <IconButton disabled={props.disabled} onClick={props.onClick} variant="outlined" color="info" size={getIconSize()}>
      <Icon icon={'arrow_left_alt'}></Icon>
    </IconButton>
  )
}

export const StyledTablePaginationMainWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 14,
  [theme.breakpoints.between(393, 'lg')]: {
    gap: 12,
  },
  [theme.breakpoints.down(393)]: {
    gap: 8,
  },
  alignItems: 'center',
  justifyContent: 'center',
}))

export const StyledStartActionComponent = styled('div')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down(393)]: {
    alignSelf: 'flex-start',
  },
}))

const StyledDisplayRowsComponentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  [theme.breakpoints.between(393, 'lg')]: {
    gap: 6,
  },
  [theme.breakpoints.down(393)]: {
    gap: 4,
  },
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledActionsComponent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  [theme.breakpoints.between(393, 'lg')]: {
    gap: 6,
  },
  [theme.breakpoints.down(393)]: {
    gap: 4,
  },
  alignItems: 'center',
  justifyContent: 'center',
}))

type DisplayRowsComponentProps = {
  from: number
  to: number
  count: number
  page: number
  rowsPerPage: number
  onRangeChange?: (index: number) => void
}

export const DisplayRowsComponent = (props: DisplayRowsComponentProps) => {
  const { count, rowsPerPage, from, to, onRangeChange } = props
  const currentRange = `${from} to ${to}`
  const ranges = getPageRanges(count, rowsPerPage)

  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down(393))
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.between(393, 'lg'))

  function getFontSize() {
    if (isMobile) return 'body3Medium'
    if (isTablet) return 'body2Medium'
    return 'body1Medium'
  }

  function getCountFontSize() {
    if (isMobile) return 'body3SemiBold'
    if (isTablet) return 'body2SemiBold'
    return 'body1SemiBold'
  }

  function getPageRanges(count, rowsPerPage) {
    const result: string[] = []
    const totalPages = Math.ceil(count / rowsPerPage)

    for (let page = 0; page < totalPages; page++) {
      const start = page * rowsPerPage + 1
      const end = Math.min((page + 1) * rowsPerPage, count)
      result.push(`${start} to ${end}`)
    }

    return result
  }

  function handleRangeChange(event) {
    const value = event.target.value
    const index = ranges.findIndex((obj) => obj === value)
    onRangeChange?.(index)
  }

  return (
    <StyledDisplayRowsComponentWrapper>
      <Select value={currentRange} onChange={handleRangeChange}>
        {ranges.map((page) => (
          <MenuItem key={page} value={page}>
            {page}
          </MenuItem>
        ))}
      </Select>
      <Typography sx={{ color: (theme) => theme.palette.neutralGrey[1000] }} variant={getFontSize()}>
        of
      </Typography>
      <Typography sx={{ color: (theme) => theme.palette.neutralGrey[1300] }} variant={getCountFontSize()}>
        {props.count}
      </Typography>
    </StyledDisplayRowsComponentWrapper>
  )
}

type ActionsComponentProps = {
  rowsPerPageOptions: (number | { value: number; label: string })[]
  onRowsPerPageChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  rowsPerPage: number
}

export const ActionsComponent = (props: ActionsComponentProps) => {
  const {
    rowsPerPageOptions = [10, 25, 50, 100],
    onRowsPerPageChange,
    rowsPerPage,
  } = props

  const pageOptions = rowsPerPageOptions.length === 0 ? [10, 25, 50, 100] : rowsPerPageOptions

  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down(393))
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.between(393, 'lg'))

  function getFontSize() {
    if (isMobile) return 'body3Medium'
    if (isTablet) return 'body2Medium'
    return 'body1Medium'
  }

  return (
    <StyledActionsComponent>
      <Typography sx={{ color: (theme) => theme.palette.neutralGrey[1000] }} variant={getFontSize()}>
        Rows per page
      </Typography>
      <Select value={rowsPerPage} onChange={onRowsPerPageChange as any}>
        {pageOptions.map((option) => {
          const value = typeof option === 'number' ? option : option.value
          const label = typeof option === 'number' ? option : option.label
          return (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          )
        })}
      </Select>
    </StyledActionsComponent>
  )
}

type TablePaginationWrapperProps = {
  startActionComponent?: TablePaginationProps['startActionComponent']
}

export const StyledTablePaginationWrapper = styled('div')<TablePaginationWrapperProps>(
  ({ startActionComponent, theme }) => ({
    display: 'flex',
    justifyContent: startActionComponent ? 'space-between' : 'flex-end',
    flexDirection: 'row',
    [theme.breakpoints.down(393)]: {
      flexDirection: 'column',
    },
    padding: 16,
    alignItems: 'center',
  })
)
